// The send doors (paperwork-1 D10, D11; L3-2; routes/document-sends.routes.js). Every answer the sheets branch on is named in
// `expected`, so a refusal the sheet renders is not a red toast as well.

import { api } from '@/lib/api.js';

const enc = encodeURIComponent;
// Every refusal a send door answers with its preview (services/standard-set-send.service.js) is named here, so the sheet's own
// message is the only one (review fix 2026-11-01: BINDER_STALE and PAYMENT_IN_FLIGHT were red-toasted on top of the sheet).
// Guard: tests/mb-send-expected-codes.test.js reads these literals and the server's refusals.
const SET_EXPECTED = [
  { status: 400, code: 'BAD_CC' },
  { status: 409, code: 'NO_POLICY_ROW' },
  { status: 409, code: 'STANDARD_SET_CHANGED' },
  { status: 409, code: 'HOLDER_CHOICE_NEEDED' }, // a holder with no email and no choice: the sheet shows the preview and the server's sentence
  { status: 409, code: 'BINDER_STALE' },
  { status: 409, code: 'NOTHING_TO_SEND' },
];
const ENDORSEMENT_EXPECTED = [
  ...['NOT_AN_ENDORSEMENT', 'TRANSACTION_VOIDED', 'TRANSACTION_DECLINED', 'NO_DEAL', 'ENDORSEMENT_NOT_SENDABLE', 'ENDORSEMENT_CHANGED'].map((code) => ({ status: 409, code })),
  { status: 400, code: 'BAD_CC' },
];
const ENDORSEMENT_SEND_EXPECTED = [
  ...ENDORSEMENT_EXPECTED,
  { status: 409, code: 'PAYMENT_IN_FLIGHT' },
  { status: 503, code: 'could_not_check' },
  { status: 413, code: 'TOO_LARGE' },
  { status: 502, code: 'SEND_FAILED' },
];

// The preview: every recipient, attachments by derived name with the original filename, missing items with their actions.
// resend: the document (or bundle inbound) ids asked to go again although delivered ("once per address, unless asked").
// forward: the holders with no email the broker chose "Client forwards it" for (David 2026-11-02); the server asks its rule with it.
export function getStandardSet(dealId, resend = [], forward = []) {
  const parts = [resend.length ? `resend=${resend.map(enc).join(',')}` : null, forward.length ? `forward=${forward.map(enc).join(',')}` : null].filter(Boolean);
  const q = parts.length ? `?${parts.join('&')}` : '';
  return api.get(`/api/tracon/deals/${enc(dealId)}/standard-set${q}`, { expected: SET_EXPECTED, timeoutMs: 60_000 });
}

// Sends the previewed set (plan_key is the preview's); answers what went to whom, read back. cc: "Add others (CC)" (round 2,
// Q21), the addresses as typed; the server validates them (400 BAD_CC) and copies them on the client's mail.
export function sendStandardSet(dealId, planKey, resend = [], cc = [], forward = []) {
  return api.post(`/api/tracon/deals/${enc(dealId)}/send-standard-set`, { plan_key: planKey, resend, cc, forward }, { expected: SET_EXPECTED, timeoutMs: 120_000 });
}

export function getEndorsementSend(transactionId) {
  return api.get(`/api/tracon/transactions/${enc(transactionId)}/send-endorsement`, { expected: ENDORSEMENT_EXPECTED });
}

export function sendEndorsement(transactionId, documentId, cc = []) {
  return api.post(`/api/tracon/transactions/${enc(transactionId)}/send-endorsement`, { document_id: documentId, cc }, { expected: ENDORSEMENT_SEND_EXPECTED, timeoutMs: 60_000 });
}

// "Confirm it is this policy's": the confirm writer (A8), the row read back with its derived name.
export function confirmDocumentForSend(documentId) {
  return api.post(`/api/tracon/documents/${enc(documentId)}/confirm`, {}, { expected: [{ status: 409, code: 'NOT_PLACED' }, { status: 409, code: 'NOT_NAMED' }, { status: 409, code: 'SUPERSEDED' }] });
}

// "Payment processing: send now" (paperwork-1 round 2, Q24): the payment as the ledger holds it, the endorsement's preview and
// whether this viewer may send; then the send with the reason (required), the document the broker saw and the person's CC.
const SEND_NOW_EXPECTED = [
  { status: 400, code: 'REASON_REQUIRED' },
  { status: 400, code: 'BAD_CC' },
  { status: 403, code: 'OWNER_OR_PRODUCER_ONLY' },
  ...['PAYMENT_NOT_PROCESSING', 'ENDORSEMENT_NOT_SENDABLE', 'ENDORSEMENT_CHANGED', 'ALREADY_CLAIMED'].map((code) => ({ status: 409, code })),
];
export function getSendBeforeClearing(transactionId) {
  return api.get(`/api/tracon/transactions/${enc(transactionId)}/send-before-clearing`, { expected: SEND_NOW_EXPECTED });
}
export function sendBeforeClearing(transactionId, { reason, documentId, cc }) {
  return api.post(`/api/tracon/transactions/${enc(transactionId)}/send-before-clearing`, { reason, document_id: documentId, cc: cc && cc.length ? cc : undefined }, { expected: [...SEND_NOW_EXPECTED, { status: 502, code: 'SEND_FAILED' }], timeoutMs: 60_000 });
}
